import { Button, Col, ConfigProvider, Divider, Drawer, Layout, Row } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined, FilterOutlined, CloseOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import './layout.css'
import { useIsSmallScreen } from "../../../utils/useMediaQuery";
import { useKeyboardAwareDrawer } from "../../../utils/useKeyboardAwareDrawer";

const { Sider } = Layout;

// Panelin kayma süresi. layout.css'teki .filter-slide-panel transition süresiyle AYNI olmalı.
const SLIDE_MS = 200;

/**
 * rememberOpen: panelin açık/kapalı durumu tarayıcıda hatırlansın mı (anahtar: sidebarTitle).
 * Grid üstünde hızlı filtre çubuğu olan sayfalar false verir: filtrelemenin asıl yeri artık çubuk, panel her
 * açılışta kapalı başlar ve eskiden kaydedilmiş anahtar ilk açılışta silinir (aşağıdaki effect).
 */
export default function LayoutWithSider({ children, sidebarTitle, sidebarComponent, fullWidth = false, rememberOpen = true }) {
    const isSmallScreen = useIsSmallScreen(992);
    const [open, setOpen] = useState(() => rememberOpen && !!localStorage.getItem(sidebarTitle));
    const [drawerOpen, setDrawerOpen] = useState(false);

    // İçerik alanının (grid'in) genişliği panelle AYNI ANDA değişmez.
    //
    // Neden: grid'in genişliği değişince sanal tablo yeniden ölçülüp render ediliyor ve bu
    // ~yarım saniye sürebiliyor (kolon sayısı × görünür satır). Bu işi tıklama ile ilk boyama
    // arasına koyarsak tıklama ölü hissediliyor; animasyonla aynı kareye koyarsak animasyon
    // duruyor (width/margin animasyonu ana iş parçacığında çalışır).
    //
    // Çözüm: panel SADECE transform ile kayar (compositor'da çalışır, ana iş parçacığı meşgul
    // olsa bile akıcı kalır), grid'in genişliği ise kayma bittikten sonra tek adımda değişir.
    // Böylece tıklamanın karşılığı anında görünür, ağır iş etkileşim penceresinin dışına çıkar.
    const [gridOpen, setGridOpen] = useState(() => rememberOpen && !!localStorage.getItem(sidebarTitle));
    const gridTimer = useRef(null);

    const toggle = useCallback((next) => {
        setOpen(next);
        if (gridTimer.current)
            clearTimeout(gridTimer.current);
        gridTimer.current = setTimeout(() => setGridOpen(next), SLIDE_MS);
    }, []);

    useEffect(() => () => clearTimeout(gridTimer.current), []);

    // mobil: sanal klavye açılınca odaklanan filtre alanı klavyenin altında kalmasın
    useKeyboardAwareDrawer(drawerOpen);

    // rememberOpen=false: anahtar hiç yazılmaz; mount'ta (open=false) eskiden kalan anahtar da silinir.
    useEffect(() => {
        if (!open || !rememberOpen) {
            localStorage.removeItem(sidebarTitle)
        }
        else {
            localStorage.setItem(sidebarTitle, '1')
        }
    }, [open]);

    // Mobile view - use Drawer
    if (isSmallScreen) {
        return (
            <>
                {sidebarComponent && (
                    <>
                        <Button
                            type="primary"
                            icon={<FilterOutlined />}
                            onClick={() => setDrawerOpen(true)}
                            style={{
                                position: 'fixed',
                                // footer sabit (--footer-h + amber çizgi) → butonu onun üstüne kaldır,
                                // yoksa footer'ı ve şirket bağlantısını örtüyor
                                bottom: 'calc(var(--footer-h) + 18px)',
                                right: 20,
                                zIndex: 1000,
                                borderRadius: '50%',
                                width: 50,
                                height: 50,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                            }}
                        />
                        <Drawer
                            title={sidebarTitle}
                            className="filter-drawer"
                            placement="left"
                            onClose={() => setDrawerOpen(false)}
                            open={drawerOpen}
                            width="85%"
                            styles={{ body: { padding: 16 } }}
                            destroyOnClose={false}
                            forceRender={true}
                        >
                            {/*
                                Açılır menüler (Select/DatePicker) varsayılan olarak <body>'ye render edilir;
                                orada kaydırma zinciri doğrudan sayfaya/grid'e geçiyor ve kullanıcı listeyi
                                kaydırırken arkadaki grid kayıyordu. Menüleri çekmece gövdesine gömüyoruz:
                                gövdede overscroll-behavior: contain var, zincir orada kesiliyor.
                                Tek tek filtrelere değil çekmeceye konur → tüm filtreler otomatik kapsanır.
                            */}
                            <ConfigProvider
                                getPopupContainer={(node) =>
                                    node?.closest?.('.ant-drawer-body') || document.body
                                }
                            >
                                {sidebarComponent}
                            </ConfigProvider>
                        </Drawer>
                    </>
                )}
                <Layout style={{ marginTop: 70, padding: '0 8px 80px 8px' }}>
                    {children}
                </Layout>
            </>
        );
    }

    // Desktop view
    // Yapı: solda her zaman duran 35px'lik şerit (Sider) + onun üzerine kayan 300px'lik panel.
    // Panel açılıp kapanırken DOM'da hiçbir şey görünüp kaybolmuyor, tek değişen transform.
    const expanded = !!(open && sidebarComponent);
    const gridExpanded = !!(gridOpen && sidebarComponent);
    return (
        <>
            {/*
                Şerit. Sider olarak bırakıldı: antd üst Layout'u Sider varlığına göre yatay eksene
                çeviriyor (.ant-layout-has-sider), plain div'e dönüştürmek sayfa iskeletini değiştirirdi.
                Genişliği artık hiç değişmiyor → açma/kapama bu elemanda layout tetiklemiyor.
            */}
            <Sider
                width={sidebarComponent ? 35 : 0}
                style={{
                    marginTop: 64,
                    overflow: 'hidden',
                    height: 'calc(100% - 64px)',
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    zIndex: 2,
                    backgroundColor: 'var(--surface)',
                    // şerit içeriğin üstünde yüzüyor (fixed); gölge onu içerikten ayırır
                    boxShadow: '2px 0 8px rgba(15, 23, 42, 0.10)'
                }}
            >
                {sidebarComponent && (
                    <>
                        <Button
                            icon={<ArrowRightOutlined />} type={'text'}
                            onClick={() => toggle(true)}
                        />
                        <Divider style={{ marginTop: 4 }} />
                        <div style={{ paddingLeft: 8, paddingRight: 8 }}>
                            {
                                // key olarak v4() kullanılmamalı: her render'da yeni key üretir,
                                // harflerin tamamı unmount/mount olurdu. İçerik sabit → index yeterli.
                                sidebarTitle && sidebarTitle.split('').reverse().map((l, i) => {
                                    return (
                                        <div key={i} className={'rotate'}>
                                            {l}
                                            <br />
                                        </div>
                                    )
                                })
                            }
                        </div>
                    </>
                )}
            </Sider>

            {/*
                Kayan panel. Kapalıyken translateX(-100%) ile ekranın dışında; display:none DEĞİL,
                yani açılırken filtrelerin baştan layout'a girmesi gerekmiyor. Animasyon yalnızca
                transform üzerinde → compositor'da çalışır, grid render'ı ana iş parçacığını
                kilitlese bile kayma akıcı görünür.
            */}
            {sidebarComponent && (
                <div className={`filter-slide-panel${expanded ? ' is-open' : ''}`}>
                    <Button
                        icon={<ArrowLeftOutlined />} type={'text'}
                        onClick={() => toggle(false)}
                    />
                    <Divider style={{ marginTop: 4 }} />
                    <div style={{ paddingLeft: 8, paddingRight: 8, minWidth: 0 }}>
                        <h3>{sidebarTitle}</h3>
                        <div style={{ height: '100%', paddingBottom: 100 }}>
                            {sidebarComponent}
                        </div>
                    </div>
                </div>
            )}
            <Layout
                style={{
                    marginTop: 80,
                    // gridOpen (open değil): kayma bitince değişir, bkz. yukarıdaki açıklama.
                    marginLeft: gridExpanded ? 332 : 48,
                    marginRight: 32,
                    marginBottom: 48,
                    // Kutu tam olarak "kalan alanı" doldursun: genişlik = %100 - (soldaki + sağdaki boşluk).
                    // Böylece toplam genişlik %100'ü aşmaz, yatay page-scroll ve panele binme olmaz.
                    width: `calc(100% - ${gridExpanded ? 364 : 80}px)`,
                    minWidth: 0,
                    // Geçiş efekti bilinçli olarak yok: margin/width animasyonu her karede
                    // grid'i yeniden ölçtürüp render ettiriyordu. Anında geçiş = tek reflow.
                }}
            >
                <Row justify={'center'}>
                    <Col xs={24} sm={24} md={24} lg={fullWidth ? 24 : 18} xl={fullWidth ? 24 : 14} xxl={fullWidth ? 24 : 12}>
                        {children}
                    </Col>
                </Row>
            </Layout>
        </>
    )
}
